import { Form, Formik } from "formik";
import { BaSeInput, BaSeOptionPicker, BaSePopupButton } from "@base/react";
import { validateCurrency } from "../../services/validate";
import FampeInputRange from "../../components/fampe-input-range";
import {
  eventTag,
  formatCurrency,
  formatFloat,
  formatPercentValue,
  isZero,
  toDecimal,
  toPercent,
} from "../../services/utils";
import { useEffect, useState } from "react";
import { toast } from "react-toastify";
import ToastContent from "../../components/toastContent";
import iconError from "../../assets/images/icons/exclamation-octagon.png";
import iconSuccess from "../../assets/images/icons/check-circle.png";
import ApiCompaniesSizes from "../../services/companiesSizes";
import Loading from "../../components/loading";
import ApiFampe from "../../services/fampe";
import { getStorage, setStorage } from "../../services/storageUtils";

export const companySizes = [
  {
    id: 99,
    label: "Microempreendedor Individual",
    sigla: "MEI",
    ceiling: 100000,
  },
  { id: 2, label: "Microempresa", sigla: "ME", ceiling: 400000 },
  { id: 3, label: "Empresa de pequeno porte", sigla: "EPP", ceiling: 700000 },
];

const fampeTooltip = `
  MEI – Teto de até R$ 100.000,00 <br />
  ME – Teto de até R$ 400.000,00 <br />
  EPP – Teto de até R$ 700.000,00
`;

const api = ApiFampe()

const Fampe = ({ account, company, event = {} }) => {
  const defaultValueForm = {
    cnpj: company || account.cpf,
    company_size: companySizes[0].id,
    percent: 80,
    financing_value: null,
    guarantees_value: null,
    fampe_guarantee: null,
    necessary_complement: null,
    limit_exceeded: 0,
  };
  const [lastValueUpdated, setLastValueUpdated] = useState(defaultValueForm);
  const [form, setForm] = useState(defaultValueForm);
  const [loading, setLoading] = useState(true);
  const [calculationDone, setCalculationDone] = useState(false);
  const [isUserAdjusting, setIsUserAdjusting] = useState(false);

  const isCpf = account.cpf;
  const hasStorageData = getStorage(`currentFampe-${form.cnpj}`, true);

  const showResults = isCpf ? calculationDone : hasStorageData;

  const getColorByPercent = () => {
    if (isLimitExceeded()) return "red";

    const value = parseInt(lastValueUpdated.percent);
    if (value === 80) return "green";
    if (value > 80) return "yellow";
    return "blue";
  };
  const setCompanySizeSelect = (data) => {
    const selectedCompanySize = companySizes.find(
      (item) => item.id === data.codPorte
    );
    if (selectedCompanySize) {
      setForm((prevForm) => ({
        ...prevForm,
        company_size: selectedCompanySize?.id,
      }));
    }
  };
  const updateValuesFampe = (data) => {
    const companyId = companySizes.find(
      (c) => c.sigla === data.company_size
    ).id;
    const ceiling = companySizes.find((c) => c.id === companyId)?.ceiling;
    const adjustedFampe = Math.min(data.fampe_guarantee, ceiling);

    setForm((prevForm) => ({
      ...prevForm,
      ...data,
      company_size: companyId,
      percent: toPercent(data.percent),
      financing_value: formatCurrency(data.financing_value) || null,
      guarantees_value: formatCurrency(data.guarantees_value) || null,
      fampe_guarantee: formatCurrency(adjustedFampe) || null,
      necessary_complement: formatCurrency(data.necessary_complement) || null,
    }));
    setLastValueUpdated((prevForm) => ({
      ...prevForm,
      ...data,
      company_size: companyId,
      percent: toPercent(data.percent),
      financing_value: formatCurrency(data.financing_value) || null,
      guarantees_value: formatCurrency(data.guarantees_value) || null,
      fampe_guarantee: formatCurrency(adjustedFampe) || null,
      necessary_complement: formatCurrency(data.necessary_complement) || null,
    }));
  };
  const getCompanySize = (cnpj) => {
    const keyStorage = `companySizes-${cnpj}`;
    const cacheCompanySize = getStorage(keyStorage, true);
    setLoading(true);
    if (cacheCompanySize) {
      setCompanySizeSelect(cacheCompanySize);
      setLoading(false);
    } else {
      ApiCompaniesSizes()
        .getCompaniesSizes(cnpj)
        .then((response) => {
          const { data } = response;
          setStorage(keyStorage, data, true);
          setCompanySizeSelect(data);
        })
        .catch((err) => console.warn(err))
        .finally(() => setLoading(false));
    }
  };
  const getCurrentFampe = (cnpj) => {
    if (!cnpj || cnpj === "0") {
      setLoading(false);
      return;
    }
    const keyStorage = `currentFampe-${cnpj}`;
    const cacheCurrentFampe = getStorage(keyStorage, true);
    setLoading(true);
    if (cacheCurrentFampe) {
      updateValuesFampe(cacheCurrentFampe);
      setCalculationDone(true);
      setLoading(false);
    } else {
      api.getFampe(cnpj)
        .then((response) => {
          const { data } = response;
          updateValuesFampe(data);
          if (!isZero(data.financing_value) && !isZero(data.guarantees_value)) {
            setCalculationDone(true);
            setStorage(keyStorage, data, true);
          }
          if (!data.company_size.length) {
            getCompanySize(cnpj);
          }
        })
        .catch((err) => {
          console.warn(err);
          getCompanySize(cnpj);

          const isFampeNotFound =
            err?.response?.status === 404 &&
            err?.response?.data?.detail?.includes("Fampe com CNPJ");

          if (!isFampeNotFound) {
            toast.error(
              <ToastContent
                title="Falha!"
                description="Ocorreu um erro ao carregar garantia. Tente novamente."
              />,
              {
                icon: () => <img src={iconError} alt="icon" />,
              }
            );
          }
        })
        .finally(() => setLoading(false));
    }
  };
  const handleUpdateFampe = () => {
    const keyStorage = `currentFampe-${form.cnpj || company}`;
    setLoading(true);

    const data = {
      cnpj: form.cnpj || company,
      company_size: companySizes.find((c) => c.id === form.company_size).sigla,
      percent: toDecimal(parseInt(form.percent)),
      financing_value: formatFloat(form.financing_value),
      guarantees_value: formatFloat(form.guarantees_value),
    };

    api.setFampe(data)
      .then((response) => {
        const { data } = response;
        updateValuesFampe(data);

        if (!isZero(data.financing_value) && !isZero(data.guarantees_value)) {
          setStorage(keyStorage, data, true);
        }

        setCalculationDone(true);

        setIsUserAdjusting(false);
        toast.success(
          <ToastContent
            title="Sucesso!"
            description="Garantia Fampe atualizada com sucesso."
          />,
          {
            icon: () => <img src={iconSuccess} alt="icon" />,
          }
        );
        eventTag(
          event?.name,
          "fampe",
          "submit",
          "Atualização de dados do FAMPE"
        );
      })
      .catch((err) => {
        console.warn(err);
        toast.error(
          <ToastContent
            title="Falha!"
            description="Ocorreu um erro ao calcular as garantias. Tente novamente."
          />,
          {
            icon: () => <img src={iconError} alt="icon" />,
          }
        );
      })
      .finally(() => setLoading(false));
  };
  const isLimitExceeded = () => {
    const company = companySizes.find(
      (c) => c.id === lastValueUpdated.company_size
    );
    const ceiling = company?.ceiling || 0;

    const fampeValue =
      typeof lastValueUpdated.fampe_guarantee === "string"
        ? parseFloat(
            lastValueUpdated.fampe_guarantee
              .replace(/[^\d,-]/g, "")
              .replace(",", ".")
          )
        : lastValueUpdated.fampe_guarantee;

    return fampeValue >= ceiling;
  };
  const getDynamicColorByPercent = () => {
    if (isUserAdjusting) {
      const value = parseInt(form.percent);
      if (value === 80) return "green";
      if (value > 80) return "yellow";
      return "blue";
    }

    if (isLimitExceeded(lastValueUpdated)) return "red";

    const value = parseInt(lastValueUpdated.percent);
    if (value === 80) return "green";
    if (value > 80) return "yellow";
    return "blue";
  };

  useEffect(() => {
    getCurrentFampe(company);

    // eslint-disable-next-line
  }, [company]);

  return loading ? (
    <div className="wrapper-loading-small">
      <Loading small />
    </div>
  ) : (
    <>
      <Formik initialValues={form} onSubmit={() => handleUpdateFampe()}>
        {({ handleSubmit }) => (
          <Form onSubmit={handleSubmit}>
            <div className="guarantees-form-area">
              <div className="tootip-title">
                <h3 className="title-fampe">Simulação de Garantia FAMPE</h3>
                <BaSePopupButton
                  title="Limites de Garantia do FAMPE por Porte da Empresa:"
                  type="popover"
                  direction="bottom"
                  iconProps={{
                    color: "#196159",
                    size: 16,
                  }}
                  message={
                    <span dangerouslySetInnerHTML={{ __html: fampeTooltip }} />
                  }
                  alertSize={300}
                />
              </div>

              <p className="description-fampe">
                Preencha os campos abaixo para fazer sua simulação:
              </p>
              <div className="first-block">
                <div className="wrapper-select">
                  <BaSeOptionPicker
                    label="Porte da empresa"
                    values={companySizes}
                    enableSelectAll={false}
                    menuAlwaysIsOpen={false}
                    withFilterText={false}
                    emptyValueLabel="Selecione"
                    maxOptionsSelected={1}
                    initialSelectedValuesId={[form.company_size]}
                    onChange={(values) => {
                      setForm((prevForm) => ({
                        ...prevForm,
                        company_size: values.map((val) => val.id)[0],
                      }));
                    }}
                  />
                </div>
                <div className="group-input">
                  <BaSeInput
                    color="default"
                    inputMode="tel"
                    typeInput="tel"
                    inputStatus={
                      form.financing_value === "0,00"
                        ? undefined
                        : validateCurrency(form.financing_value)
                        ? "invalid"
                        : "valid"
                    }
                    value={form.financing_value}
                    onChange={(e) => {
                      setForm({ ...form, financing_value: e });
                    }}
                    placeholder="R$"
                    maxLength={22}
                    prefix="R$ "
                    mask="decimal"
                    size="medium"
                    label="Valor Financiamento"
                    width="100%"
                  />
                </div>
              </div>
              <div className="group-input">
                <BaSeInput
                  color="default"
                  inputMode="decimal"
                  typeInput="tel"
                  inputStatus={
                    form.financing_value === "0,00"
                      ? undefined
                      : validateCurrency(form.financing_value)
                      ? "invalid"
                      : "valid"
                  }
                  value={form.guarantees_value}
                  onChange={(e) => {
                    setForm({ ...form, guarantees_value: e });
                  }}
                  placeholder="R$"
                  maxLength={22}
                  prefix="R$ "
                  mask="decimal"
                  size="medium"
                  label="Valor garantias exigidas pelo banco"
                  width="100%"
                />
              </div>
              <div className="fampe-range">
                <FampeInputRange
                  legacyStyle={false}
                  value={form.percent}
                  setValue={(e) => {
                    setIsUserAdjusting(true);
                    setForm((prevForm) => ({ ...prevForm, percent: e }));
                  }}
                  label="Qual a porcentagem de garantia FAMPE você gostaria de calcular?"
                  themeColor={getDynamicColorByPercent()}
                />
              </div>
            </div>

            <div className="btn-area">
              <button
                type="submit"
                onClick={() => handleSubmit}
                disabled={
                  isZero(form.financing_value) || isZero(form.guarantees_value)
                }
                className="btn-green"
              >
                {!showResults ? "Simular" : "Atualizar valores"}
              </button>
            </div>
          </Form>
        )}
      </Formik>
      {showResults && (
        <div className="results">
          <p>Com base nas informações fornecidas, foram calculados:</p>
          <div className="results__blocks">
            <div className="left-block-results">
              <div className={"group-input " + getColorByPercent()}>
                <div
                  className={
                    "input-error-message " +
                    (isLimitExceeded() ? "invalid" : "")
                  }
                >
                  <BaSeInput
                    key={form.fampe_guarantee}
                    color="default"
                    inputMode="tel"
                    typeInput="tel"
                    value={form.fampe_guarantee}
                    placeholder="R$"
                    maxLength={22}
                    prefix="R$ "
                    mask="decimal"
                    size="medium"
                    label="Garantia Fampe"
                    width="100%"
                    isReadOnly={true}
                  />
                </div>
              </div>
              <div className={"info-percent " + getColorByPercent()}>
                {isLimitExceeded() ? (
                  <h5 className="mb-0">
                    O limite por operação para o porte selecionado foi atingido
                    e corresponde a{" "}
                    {formatPercentValue(lastValueUpdated.limit_exceeded)} do
                    financiamento.
                  </h5>
                ) : (
                  <h5>O limite padrão de garantias do FAMPE é de 80%.</h5>
                )}
                {!isLimitExceeded() && (
                  <>
                    {parseInt(lastValueUpdated.percent) === 80 && (
                      <p>
                        Você tem grandes chances de conseguir o aval com esse
                        valor.
                      </p>
                    )}
                    {parseInt(lastValueUpdated.percent) > 80 && (
                      <p>
                        Situações excepcionais podem contemplar até 100%.
                        Verifique o seu enquadramento{" "}
                        <strong>
                          <a
                            href="https://sebrae.com.br/sites/PortalSebrae/fampe"
                            target="_blank"
                            rel="noreferrer"
                          >
                            aqui
                          </a>
                        </strong>
                        .
                      </p>
                    )}
                    {parseInt(lastValueUpdated.percent) < 80 && (
                      <p>
                        Tem certeza de que deseja um valor menor? Verifique se
                        essa opção atende às suas necessidades.
                      </p>
                    )}
                  </>
                )}
              </div>
            </div>
            <div className="right-block-results">
              <div className="group-input gray-green">
                <BaSeInput
                  key={lastValueUpdated.necessary_complement}
                  color="default"
                  inputMode="tel"
                  typeInput="tel"
                  value={lastValueUpdated.necessary_complement}
                  placeholder="R$"
                  maxLength={22}
                  prefix="R$ "
                  mask="decimal"
                  size="medium"
                  label="Complemento necessário de garantias"
                  width="100%"
                  isReadOnly={true}
                />
              </div>
              <small>
                <strong>Importante:</strong> esta é apenas uma simulação com
                base nas informações fornecidas. Ela não representa uma oferta
                de crédito nem garante a concessão de recursos pelo{" "}
                <strong>FAMPE</strong> ou instituições financeiras parceiras.
              </small>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default Fampe;
